import { useMemo } from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { QueryRowType } from "@rocicorp/zero";
import { createId } from "@corpuscomplete/core/utils";
import { AgentSession } from "@corpuscomplete/core/ai/agent-session";
import { MealPlanningSessionPlannerAgent } from "@corpuscomplete/core/nutrition/meal-planning-agent";
import type { MealPlanningSession } from "@corpuscomplete/core/nutrition/meal-planning-session";
import { WorkflowExecution } from "@corpuscomplete/core/ai/workflow";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { ActionAvailability } from "../../../common/action-availability";
import type { AgentTools } from "../../../ai/agent/tools";

export namespace MealPlanningSessionPlanner {
  type SessionRow = NonNullable<QueryRowType<typeof MealPlanningSessionPlannerAgent.session>>;

  type MealWorkflowExecutionLike = {
    workflowExecutionId: string;
    plannedMealId: string;
    status: WorkflowExecution.Status;
    timeCreated: Temporal.Instant;
  };

  export type MealWorkflowProgressSummary = {
    totalMeals: number;
    completedMeals: number;
    failedMeals: number;
    progressPercent: number;
    hasActiveWork: boolean;
    hasFailedWork: boolean;
  };

  type StartUnavailableReason =
    | ActionAvailability.MissingPermissionReason
    | MealPlanningSessionPlannerAgent.CreateUnavailableReason;

  export const toolName = "proposePlan";

  export const deferredToolPresenter: AgentTools.DeferredToolPresenter = {
    isDeferredToolPart: isProposePlanPart,
    getDeferredToolLabel: (part) => (part && isProposePlanPart(part) ? "Approve plan" : undefined),
  };

  export function useSession(mealPlanningSessionId: string): SessionRow | undefined {
    const [row] = useQuery(queries.mealPlanningAgent.plannerSession({ mealPlanningSessionId }));
    return row;
  }

  export function useCreateSession() {
    const z = useZero();
    return (params: { mealPlanningSessionId: string }) => {
      const args = AgentSession.createArgs(MealPlanningSessionPlannerAgent.definition, {
        params: { ...params, initialMessageId: createId() },
      });
      return z.mutate(mutators.agentSession.create(args));
    };
  }

  export function useMealWorkflowProgressSummary(args: {
    plannerSession: SessionRow | undefined;
    plannedMeals: readonly Pick<MealPlanningSession.Info["plannedMeals"][number], "id">[];
  }): MealWorkflowProgressSummary | null {
    const byMealId = useMemo(
      () => groupWorkflowExecutionsByMeal(args.plannerSession?.workflowExecutions ?? []),
      [args.plannerSession?.workflowExecutions],
    );
    return useMemo(
      () => deriveMealWorkflowProgressSummary({ plannedMeals: args.plannedMeals, byMealId }),
      [args.plannedMeals, byMealId],
    );
  }

  function groupWorkflowExecutionsByMeal(
    workflowExecutions: SessionRow["workflowExecutions"],
  ): Record<string, MealWorkflowExecutionLike[]> {
    const grouped: Record<string, MealWorkflowExecutionLike[]> = {};
    for (const link of workflowExecutions) {
      if (!link.base) continue;
      const byMeal = grouped[link.plannedMealId] ?? [];
      byMeal.push({
        workflowExecutionId: link.id,
        plannedMealId: link.plannedMealId,
        status: link.base.status,
        timeCreated: link.base.timeCreated,
      });
      grouped[link.plannedMealId] = byMeal;
    }
    for (const entries of Object.values(grouped)) {
      entries.sort((a, b) => {
        if (Temporal.Instant.compare(a.timeCreated, b.timeCreated) === 0) {
          return a.workflowExecutionId.localeCompare(b.workflowExecutionId);
        }
        return Temporal.Instant.compare(a.timeCreated, b.timeCreated) < 0 ? 1 : -1;
      });
    }
    return grouped;
  }

  export function deriveMealWorkflowProgressSummary(args: {
    plannedMeals: readonly Pick<MealPlanningSession.Info["plannedMeals"][number], "id">[];
    byMealId: Record<string, readonly MealWorkflowExecutionLike[]>;
  }): MealWorkflowProgressSummary | null {
    if (args.plannedMeals.length === 0) return null;
    let hasAnyWorkflow = false;
    let completedMeals = 0;
    let failedMeals = 0;
    let hasActiveWork = false;
    for (const meal of args.plannedMeals) {
      const latestExecution = getLatestWorkflowExecution(args.byMealId[meal.id]);
      if (!latestExecution) continue;
      hasAnyWorkflow = true;
      if (latestExecution.status === "completed") completedMeals += 1;
      if (
        latestExecution.status === "completed_with_errors" ||
        latestExecution.status === "error" ||
        latestExecution.status === "canceled"
      ) {
        failedMeals += 1;
      }
      if (WorkflowExecution.isActiveStatus(latestExecution.status)) hasActiveWork = true;
    }
    if (!hasAnyWorkflow) return null;
    return {
      totalMeals: args.plannedMeals.length,
      completedMeals,
      failedMeals,
      progressPercent: Math.round((completedMeals / args.plannedMeals.length) * 100),
      hasActiveWork,
      hasFailedWork: failedMeals > 0,
    };
  }

  function getLatestWorkflowExecution(
    executions: readonly MealWorkflowExecutionLike[] | undefined,
  ): MealWorkflowExecutionLike | undefined {
    if (!executions?.length) return undefined;
    const first = executions[0];
    if (!first) return undefined;
    return executions.reduce((latest, candidate) => {
      if (Temporal.Instant.compare(candidate.timeCreated, latest.timeCreated) > 0) return candidate;
      if (Temporal.Instant.compare(candidate.timeCreated, latest.timeCreated) === 0) {
        return candidate.workflowExecutionId > latest.workflowExecutionId ? candidate : latest;
      }
      return latest;
    }, first);
  }

  export function getStartAvailability(args: {
    canEditSessionMeals: boolean;
    session: Pick<MealPlanningSession.Info, "group" | "plannedMeals"> | null | undefined;
    plannerSessionExists: boolean;
    memberContextsComplete: boolean;
  }): ActionAvailability<StartUnavailableReason> {
    if (!args.canEditSessionMeals) {
      return ActionAvailability.unavailable(ActionAvailability.MISSING_PERMISSION);
    }
    const reason = MealPlanningSessionPlannerAgent.getCreateUnavailableReason(args);
    return reason ? ActionAvailability.unavailable(reason) : ActionAvailability.available();
  }

  export function isProposePlanPart(
    part: AgentTools.DeferredTool,
  ): part is AgentTools.DeferredTool & {
    type: "tool-proposePlan";
    toolCallId: string;
    input?: { meals: MealPlanningSessionPlannerAgent.ProposedMeal[] };
    output?: { meals: MealPlanningSessionPlannerAgent.ProposedMeal[] };
  } {
    return part.type === "tool-proposePlan";
  }

  export function isProposePlanCompletedPart(part: AgentTools.DeferredTool): boolean {
    return isProposePlanPart(part) && "output" in part;
  }

  export function formatProposalSummary(
    input: { meals: MealPlanningSessionPlannerAgent.ProposedMeal[] } | undefined,
    session: Pick<MealPlanningSession.Info, "group" | "scheduleConfig"> | undefined,
  ): string {
    const meals = Array.isArray(input?.meals) ? input.meals : [];
    if (!meals.length) return "No proposed meals";
    return meals.map((meal) => formatMealSummary(meal, session)).join("; ");
  }

  function formatMealSummary(
    meal: MealPlanningSessionPlannerAgent.ProposedMeal,
    session: Pick<MealPlanningSession.Info, "group" | "scheduleConfig"> | undefined,
  ): string {
    const slotName =
      resolveSlotName(session?.scheduleConfig?.data, meal) ?? meal.mealScheduleSlotId;
    const memberNames = meal.plates.map((plate) => {
      const member = session?.group?.members.find((candidate) => candidate.userId === plate.userId);
      return member?.profile?.name ?? plate.userId;
    });
    const eventLabel = meal.events.length === 1 ? "1 time" : `${meal.events.length} times`;
    return `${meal.name || "Unnamed meal"} (${slotName}, ${memberNames.join(", ")}, ${eventLabel})`;
  }

  function resolveSlotName(
    scheduleConfig: MealPlanningSession.ScheduleConfigData | null | undefined,
    meal: Pick<
      MealPlanningSessionPlannerAgent.ProposedMeal,
      "mealScheduleId" | "mealScheduleSlotId"
    >,
  ): string | undefined {
    const assignment = scheduleConfig?.assignments.find(
      (candidate) => candidate.mealScheduleId === meal.mealScheduleId,
    );
    return assignment?.slots.find((slot) => slot.mealScheduleSlotId === meal.mealScheduleSlotId)
      ?.name;
  }
}
